// screens-blog.tsx - Blog index, post template, changelog
// Exports: ScreenBlog, ScreenBlogPost, ScreenChangelog, BLOG_POSTS, CHANGELOG_RELEASES
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '../design/router';
import { Arrow, MarketingFooter, MarketingNav, SignupCta } from '../components/ui-shared';
import { ImageSlot } from '../components/ImageSlot';

export interface BlogPost {
  id: string;
  title: string;
  tag: string;
  when: string;
  read: string;
  blurb: string;
  author: string;
  authorInitials: string;
  body: ReactNode;
}

export const BLOG_POSTS: BlogPost[] = [
  {
    id: 'real-apps', title: 'A link is not the finish line', tag: 'Product', when: '5 Sep 2026', read: '4 min',
    blurb: 'Why every published Studio game is hosted, versioned and installable, and what that changes for the people who play it.',
    author: 'R. Fuller', authorInitials: 'RF',
    body: <>
      <p>Browser game tools usually stop at a shareable link. That is a fine demo, but it is not a finished game. The link dies when the host does, players cannot find it again, and it never feels like something they own.</p>
      <h2>What publishing does now</h2>
      <p>Pressing Publish in Studio snapshots the project and puts it live at a permanent address on infrastructure we run. The address stays with the project across every version. Players open it in a browser, or add it to a phone home screen or a desktop, where it launches full screen with the game's own name and icon.</p>
      <h2>Versions, kept</h2>
      <p>Every publish keeps a note and a snapshot. Restore an older version into the editor, compare what changed, and nothing goes live until you publish again. The address never moves.</p>
      <h2>Why it matters</h2>
      <p>The best test of a tool is whether the thing it produces stands on its own. A hosted, installable, versioned game does. A link does not.</p>
    </>,
  },
  {
    id: 'two-multiplayer-models', title: 'Two kinds of multiplayer, and which one your game needs', tag: 'Engineering', when: '2 Sep 2026', read: '6 min',
    blurb: 'Every published project can sync players automatically. Sunder runs something heavier. Here is the difference, and why both exist.',
    author: 'R. Fuller', authorInitials: 'RF',
    body: <>
      <p>Set a project to Co-op or Versus and players who open your link see each other, see each other move, and share whatever your rules decide. Nothing to author. That is the relay model, and it suits co-op, exploration and turn-based games well.</p>
      <h2>What the relay does not do</h2>
      <p>The relay forwards what players send. It does not simulate movement or check hits, so it is not a referee. One player runs the rules on behalf of the room and hands over if they leave. For friends playing together, that is exactly enough.</p>
      <h2>What Sunder does instead</h2>
      <p>Sunder is a competitive shooter, so it runs a dedicated server that simulates the world and answers with the truth. Prediction keeps movement instant and reconciliation quietly corrects disagreements. What you see is what the server saw.</p>
      <p>That server tier exists on the platform today for Sunder. Bringing it to every project is a larger piece of work than presence and shared state, and it is the direction for competitive projects.</p>
    </>,
  },
  {
    id: 'editor', title: 'The editor is the game, paused', tag: 'Product', when: '15 May 2026', read: '5 min',
    blurb: 'No mode switches, no rebuilds. The thinking behind live-world editing.',
    author: 'S. Adeyemi', authorInitials: 'SA',
    body: <>
      <p>Most game editors are a separate application that happens to produce your game. Ours is not. Edit, Preview and the published build all run the same renderer and the same scene. Preview is Edit with the camera let loose.</p>
      <h2>Why that matters</h2>
      <p>There is no export step between placing an object and seeing it behave. Move a light, adjust a trigger volume, wire a rule, press Preview and you are standing in the result, not waiting for a build to catch up.</p>
      <h2>What it costs</h2>
      <p>Keeping one runtime honest across authoring and play is harder than maintaining two. Every editor feature has to work inside the same constraints the shipped game runs under, which is exactly the discipline that keeps Preview from lying to you.</p>
      <p>It is also why we can say a project you preview is the project your players will see, not an approximation of it.</p>
    </>,
  },
  {
    id: 'biped', title: 'One rig to pose them all', tag: 'Design', when: '2 May 2026', read: '6 min',
    blurb: 'How the neutral biped keeps character work fast without locking your art style.',
    author: 'R. Fuller', authorInitials: 'RF',
    body: <>
      <p>Every character in Studio, however it is modelled or textured, maps onto one neutral biped skeleton. That single decision is what makes the animation library reusable.</p>
      <h2>Shared clips, not shared looks</h2>
      <p>A shared library of 45 clips covers movement, traversal, combat, reactions and social gestures. Because the mapping is skeleton-based rather than mesh-based, a blocky placeholder and a fully sculpted custom model both drive the same walk cycle without either one dictating the other's proportions.</p>
      <h2>Bringing your own</h2>
      <p>Upload a rigged model and map its bones onto the shared skeleton, or place a few landmarks on an unrigged mesh and let auto-rig build the skeleton for you. Either way, the whole clip set plays on your art in Animation Studio, in Preview and in the published game.</p>
      <p>The trade-off is real: anything that cannot be expressed on a humanoid skeleton is out of scope for now. For the overwhelming majority of characters people actually ship, that has not been the limiting factor.</p>
    </>,
  },
];

export function ScreenBlog() {
  const featured = BLOG_POSTS[0]!;
  const rest = BLOG_POSTS.slice(1);
  return (
    <div className="ss-screen">
      <MarketingNav />
      <header style={{ padding: '88px clamp(20px, 6vw, 56px) 48px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span className="ss-eyebrow">Blog</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 8.5vw, 3.375rem)' }}>Notes from the stack</h1>
        </div>
      </header>
      <section style={{ padding: '0 clamp(20px, 6vw, 56px) 110px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Link to={`/blog/post/${featured.id}`} className="ss-card ss-grid-collapse" style={{ padding: 12, display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 28, alignItems: 'center', color: 'var(--ss-text)' }}>
            <ImageSlot shape="rounded" radius={12} placeholder="Featured post image"
              style={{ width: '100%', height: 300, display: 'block' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingRight: 24 }}>
              <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-accent)' }}>{featured.tag}</span>
              <h2 className="ss-display" style={{ fontSize: 30 }}>{featured.title}</h2>
              <p style={{ fontSize: 14.5, color: 'var(--ss-text-2)', lineHeight: 1.6 }}>{featured.blurb}</p>
              <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>{featured.when} · {featured.read} read</span>
            </div>
          </Link>
          <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {rest.map((p) => (
              <Link key={p.id} to={`/blog/post/${p.id}`} className="ss-card" style={{ padding: '24px 24px 26px', display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--ss-text)' }}>
                <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-accent)' }}>{p.tag}</span>
                <h3 className="ss-display" style={{ fontSize: 20 }}>{p.title}</h3>
                <p style={{ fontSize: 13.5, color: 'var(--ss-text-2)', lineHeight: 1.55, flex: 1 }}>{p.blurb}</p>
                <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>{p.when} · {p.read} read</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

/* ── Post template ────────────────────────────────────────────────────── */
export function ScreenBlogPost() {
  const { id } = useParams<{ id: string }>();
  const post = BLOG_POSTS.find((p) => p.id === id);

  if (!post) {
    return (
      <div className="ss-screen">
        <MarketingNav />
        <article style={{ padding: '110px clamp(20px, 6vw, 56px)' }}>
          <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <span className="ss-eyebrow">Blog</span>
            <h1 className="ss-display" style={{ fontSize: 32 }}>That post does not exist</h1>
            <p style={{ fontSize: 14.5, color: 'var(--ss-text-2)' }}>It may have moved, or the link was mistyped.</p>
            <Link to="/blog" style={{ fontSize: 13.5, color: 'var(--ss-text-2)', display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}><Arrow /></span> All posts
            </Link>
          </div>
        </article>
        <MarketingFooter />
      </div>
    );
  }

  return (
    <div className="ss-screen">
      <MarketingNav />
      <article style={{ padding: '80px clamp(20px, 6vw, 56px) 110px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
            <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-accent)' }}>{post.tag}</span>
            <h1 className="ss-display" style={{ fontSize: 'clamp(1.8rem, 7.5vw, 2.75rem)' }}>{post.title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="app-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>{post.authorInitials}</div>
              <span style={{ fontSize: 13, color: 'var(--ss-text-3)' }}>{post.author} · {post.when} · {post.read} read</span>
            </div>
          </div>
          <ImageSlot shape="rounded" radius={14} placeholder="Post hero image"
            style={{ width: '100%', height: 360, display: 'block', marginBottom: 36 }} />
          <div className="doc-prose" style={{ fontSize: 16, lineHeight: 1.8 }}>
            {post.body}
          </div>
          <hr className="ss-hairline" style={{ margin: '40px 0 24px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link to="/blog" style={{ fontSize: 13.5, color: 'var(--ss-text-2)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}><Arrow /></span> All posts
            </Link>
            <SignupCta label="Start building" />
          </div>
        </div>
      </article>
      <MarketingFooter />
    </div>
  );
}

/* ── Changelog ────────────────────────────────────────────────────────── */
export type ChangelogKind = 'New' | 'Improved' | 'Fixed';

function ClTag({ kind }: { kind: ChangelogKind }) {
  const colours: Record<ChangelogKind, [string, string]> = { New: ['var(--ss-success)', 'var(--ss-success-soft)'], Improved: ['var(--ss-accent)', 'var(--ss-accent-border)'], Fixed: ['var(--ss-text-2)', 'var(--ss-border)'] };
  const [c, b] = colours[kind];
  return <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: c, border: '1px solid ' + b, borderRadius: 999, padding: '3px 9px', flexShrink: 0 }}>{kind}</span>;
}

export interface ChangelogRelease {
  v: string;
  when: string;
  items: [ChangelogKind, string][];
}

/** The creator-facing summary of each Studio release. The engineering
 * changelog (CHANGELOG.md in the repository) is the full record; this list
 * is what a creator needs to know changed for them. */
export const CHANGELOG_RELEASES: ChangelogRelease[] = [
  { v: 'Studio 1.5', when: '5 Sep 2026', items: [
    ['New', 'Five named workspaces replace the mode rail: Scene, Art & Assets, Systems, Setup and Code. Panels dock and remember their layout per project.'],
    ['New', 'Code workspace: a real multi-file HollowScript editor with diagnostics, completion, run on start and a Run button wired to Preview.'],
    ['New', 'A marketing page for each Soulstack Original, and the Showcase as the single place to browse games.'],
    ['Improved', 'Scene workspace rebuilt around one layout: viewport with its tools docked above, structure on the left, properties on the right.'],
    ['Improved', 'Camera easing in the editor viewport no longer snaps when a character changes facing.'],
    ['Fixed', 'The viewport toolbar no longer paints over the panels beside it on narrow layouts.'],
    ['Fixed', 'Players and Publish pages no longer assume every project is a competitive match game; Solo and Co-op projects show what applies to them.'],
  ]},
  { v: 'Studio 1.4', when: 'Sep 2026', items: [
    ['New', 'Logic rules, trigger zones, win conditions and health run in every published game, not only in Sunder.'],
    ['New', 'Multiplayer for every project: a presence list, live player movement and shared rule state, with nothing to author.'],
    ['New', 'Published games are installable: add them to a phone home screen or install on desktop.'],
    ['New', 'Wikis: a multi-page markdown wiki per game at its own address, with a public directory.'],
    ['New', 'Project templates: Gallery, Village and Platformer, plus import from an exported project file.'],
    ['New', 'Map Builder: draw rooms and place stairs across multiple floors.'],
    ['New', 'Pixel Studio: spritesheet import with automatic slicing, baked tile art, and entity placement with patrols.'],
    ['New', 'Intro Sequences: a layered timeline for the first seconds of every session.'],
    ['New', 'Cutscenes play in Preview and in published games, with actor clip tracks and a camera that can track an actor.'],
    ['New', 'Gunsmith Kit: assemble, skin and voice weapons for 3D projects.'],
    ['Improved', 'Editor presence: collaborators\' cursors are real cross-user presence, not a same-browser simulation.'],
    ['Improved', 'Loading, empty and error states made consistent across the whole app.'],
    ['Fixed', 'Placed characters and patrols now reach players in published games.'],
    ['Fixed', 'Custom character poses load with the project instead of being replaced by the stock library.'],
  ]},
  { v: 'Studio 1.3', when: '23 Aug 2026', items: [
    ['New', 'A cutscene camera can track a live actor instead of only panning to a fixed point.'],
  ]},
  { v: 'Studio 1.2', when: '23 Aug 2026', items: [
    ['Fixed', 'Stairs climb a step at a time when a tread count is set, rather than a smooth ramp.'],
    ['Fixed', 'Systems workspace grouped into tabs instead of a sheer volume of closeable panels.'],
    ['Fixed', 'First-person controls respect a cutscene camera override for key presses as well as movement.'],
  ]},
];

export function ScreenChangelog() {
  const releases = CHANGELOG_RELEASES;
  return (
    <div className="ss-screen">
      <MarketingNav />
      <header style={{ padding: '88px clamp(20px, 6vw, 56px) 40px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span className="ss-eyebrow">Changelog</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 8.5vw, 3.375rem)' }}>What's new</h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 15.5 }}>What changed for creators and players in each Studio release.</p>
        </div>
      </header>
      <section style={{ padding: '0 clamp(20px, 6vw, 56px) 110px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {releases.map((r) => (
            <div key={r.v} className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 28, padding: '30px 0', borderTop: '1px solid var(--ss-border-soft)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 15.5, fontWeight: 600 }}>{r.v}</span>
                <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11, color: 'var(--ss-text-3)' }}>{r.when}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {r.items.map(([kind, text]) => (
                  <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <ClTag kind={kind} />
                    <span style={{ fontSize: 14, color: 'var(--ss-text-2)', lineHeight: 1.55 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
